import { normalizeProductCategoryId } from './categoryTypes'
import type { CategoryRecord } from './categoryTypes';
import type { ProductCatalogEventRow } from './catalogTypes';

export type CategoryDisplayResolver = (categoryId: string | null | undefined) => string;

const CATEGORY_LINE_RE = /Categoría:\s*([^·→]+?)\s*→\s*([^·]+?)(?=\s*·|$)/;
const CAT_UUID_RE = /\b(cat-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/gi;

export function createCategoryDisplayResolver(categories: CategoryRecord[]): CategoryDisplayResolver {
  // Pre-build Maps for O(1) lookup instead of .find() per call.
  const bySlug = new Map<string, string>();
  const byId = new Map<string, string>();
  const byRaw = new Map<string, string>();
  for (const c of categories) {
    const slugNorm = normalizeProductCategoryId(c.slug);
    const idNorm = normalizeProductCategoryId(c.id);
    if (c.name) {
      if (slugNorm) bySlug.set(slugNorm, c.name);
      if (idNorm) byId.set(idNorm, c.name);
    }
    if (c.slug) byRaw.set(c.slug, c.name ?? c.slug);
    if (c.id) byRaw.set(c.id, c.name ?? c.id);
  }
  return (categoryId) => {
    if (!categoryId || !String(categoryId).trim()) return '—';
    const normalized = normalizeProductCategoryId(categoryId);
    return bySlug.get(normalized) ?? byId.get(normalized) ?? byRaw.get(String(categoryId).trim()) ?? String(categoryId).trim();
  };
}

function resolveSegmentLabel(resolver: CategoryDisplayResolver, segment: string): string {
  const trimmed = segment.trim();
  if (trimmed === '—' || trimmed === '') return '—';
  return resolver(trimmed);
}

export function formatProductCatalogEventSummary(
  row: ProductCatalogEventRow,
  resolveCategory: CategoryDisplayResolver
): string {
  let text = row.summary?.trim() || '—';

  const payload = row.payload;
  if (row.eventType === 'update' && payload && typeof payload === 'object') {
    const before = payload.before as { categoryId?: string | null } | undefined;
    const after = payload.after as { categoryId?: string | null } | undefined;
    if (before && after && before.categoryId !== after.categoryId) {
      const beforeLabel = before.categoryId ? resolveCategory(before.categoryId) : '—';
      const afterLabel = after.categoryId ? resolveCategory(after.categoryId) : '—';
      const categoryPart = `Categoría: ${beforeLabel} → ${afterLabel}`;
      if (CATEGORY_LINE_RE.test(text)) {
        text = text.replace(CATEGORY_LINE_RE, categoryPart);
      }
    }
  }

  text = text.replace(CAT_UUID_RE, (match) => {
    const resolved = resolveCategory(match);
    return resolved === match ? match : resolved;
  });

  return text;
}
